import { useEffect, useState } from "react";
import { Pencil, Save } from "lucide-react";
import type { AirportConfigSet, AirportConfigUpdate, GlobalEvent, WorldData } from "../../../domain/types";
import { buildAirportFuelQuote } from "../../../domain/rules/airport";
import { airportCapacityMultiplier, eventMatchesAirport } from "../../../domain/rules/events";
import { MiniStat, airportConfigUpdateFromSet, matchesQuery } from "./GmShared";


export function AirportConfigPanel({
  world,
  airportConfigs,
  activeEvents,
  onSaveAirportConfig
}: {
  world: WorldData;
  airportConfigs: AirportConfigSet;
  activeEvents: GlobalEvent[];
  onSaveAirportConfig: (input: AirportConfigUpdate) => void;
}) {
  const [airportCode, setAirportCode] = useState(world.airports[0]?.code ?? "");
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState<AirportConfigUpdate>(() => airportConfigUpdateFromSet(world, airportConfigs, airportCode));

  useEffect(() => {
    const selectedExists = world.airports.some((airport) => airport.code === airportCode);
    if (!selectedExists && world.airports[0]) {
      setAirportCode(world.airports[0].code);
    }
  }, [airportCode, world.airports]);

  useEffect(() => {
    setDraft(airportConfigUpdateFromSet(world, airportConfigs, airportCode));
  }, [airportCode, world, airportConfigs]);

  const selectedAirport = world.airports.find((airport) => airport.code === airportCode) ?? world.airports[0];
  const filteredAirports = world.airports.filter((airport) =>
    matchesQuery([airport.code, airport.icao, airport.nameZh, airport.cityZh, airport.countryZh, airport.regionId, airport.fuelZoneId], query)
  );
  const closedCount = airportConfigs.ops.filter((config) => config.closed).length;
  const noFuelCount = airportConfigs.fuel.filter((config) => !config.fuelAvailable).length;
  const averageFuelPrice = averageAirportFuelPrice(airportConfigs.fuel);
  const selectedEvents = airportEvents(selectedAirport, activeEvents);
  const selectedFuelQuote = airportFuelQuote(selectedAirport, activeEvents, airportConfigs);
  const selectedCapacityMultiplier = selectedAirportCapacityMultiplier(selectedAirport, activeEvents);
  const groundModel = airportGroundModel(draft);

  if (!selectedAirport) {
    return <div className="empty-state">当前世界资料没有机场，无法配置机场运行数据。</div>;
  }

  return (
    <div className="gm-airport-program">
      <div className="gm-kpi-strip">
        <MiniStat label="机场配置" value={airportConfigs.ops.length} />
        <MiniStat label="关闭机场" value={closedCount} tone={closedAirportTone(closedCount)} />
        <MiniStat label="停供机场" value={noFuelCount} tone={noFuelAirportTone(noFuelCount)} />
        <MiniStat label="平均油价" value={`$${averageFuelPrice}/t`} />
      </div>

      <div className="gm-airport-body">
        <section className="gm-data-board">
          <div className="gm-board-header">
            <strong>机场基础配置</strong>
            <label>
              筛选
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="代码/城市/区域/油区" />
            </label>
          </div>
          <div className="table-wrap compact">
            <table>
              <thead>
                <tr>
                  <th>机场</th>
                  <th>运行</th>
                  <th>油价/供油</th>
                  <th>业载能力</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {filteredAirports.map((airport) => {
                  const update = airportConfigUpdateFromSet(world, airportConfigs, airport.code);
                  return (
                    <tr key={airport.code} className={airportConfigRowClassName(airport.code, airportCode)}>
                      <td>{airport.code} / {airport.icao}<br />{airport.cityZh}{airport.nameZh}</td>
                      <td>
                        <span className={`status-pill ${airportOpsStatusTone(update.ops.closed)}`}>{airportOpsStatusLabel(update.ops.closed)}</span>
                        <br />
                        出{update.ops.departureCapacity}/进{update.ops.arrivalCapacity} / 过站{update.ops.minimumTurnMinutes}m
                      </td>
                      <td>
                        <span className={`status-pill ${airportFuelStatusTone(update.fuel.fuelAvailable)}`}>{airportFuelStatusLabel(update.fuel.fuelAvailable)}</span>
                        <br />
                        {airportFuelSummary(update)}
                      </td>
                      <td>{update.payload.baseDepartureDemand} pax/day / 货邮 x{update.payload.cargoDemandIndex}</td>
                      <td>
                        <button onClick={() => setAirportCode(airport.code)}>
                          <Pencil size={12} />
                          编辑
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filteredAirports.length === 0 && (
                  <tr>
                    <td colSpan={5}>没有匹配的机场配置。</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="gm-form-section gm-airport-editor">
          <strong>{selectedAirport.code} / {selectedAirport.cityZh}机场运行数据</strong>
          <div className="gm-airport-effective">
            <span>事件后容量 x{selectedCapacityMultiplier.toFixed(2)}</span>
            <span>{selectedFuelPriceLabel(selectedFuelQuote)}</span>
            <span>{selectedFuelAvailabilityLabel(selectedFuelQuote)}</span>
            <span>{selectedAirportEventLabel(selectedEvents.length)}</span>
          </div>
          <div className="gm-ground-model-strip">
            <span>地服效率 {groundModel.serviceFactor}</span>
            <span>10t 加油 {groundModel.fuel10tMinutes}</span>
            <span>10t 抽油 {groundModel.defuel10tMinutes}</span>
            <span>180 pax 登机 {groundModel.paxWaveMinutes}</span>
            <span>10t 货邮 {groundModel.cargo10tMinutes}</span>
            <span>随机延误 {groundModel.delayModel}</span>
            <span>节点按 movement 独立生成：上水 / 餐食 / 旅客 / 行李 / 货邮 / 舱单 / 撤轮挡 / 推出 / 滑行</span>
          </div>
          <div className="gm-airport-form">
            <label className="check-row">
              <input type="checkbox" checked={draft.ops.closed} onChange={(event) => setDraft((current) => ({ ...current, ops: { ...current.ops, closed: event.target.checked } }))} />
              机场关闭
            </label>
            <label className="check-row">
              <input type="checkbox" checked={draft.fuel.fuelAvailable} onChange={(event) => setDraft((current) => ({ ...current, fuel: { ...current.fuel, fuelAvailable: event.target.checked } }))} />
              机场供油
            </label>
            <label>
              Slot 容量
              <input type="number" min={0} value={draft.ops.slotCapacity} onChange={(event) => setDraft((current) => ({ ...current, ops: { ...current.ops, slotCapacity: Number(event.target.value) } }))} />
            </label>
            <label>
              出港容量/h
              <input type="number" min={0} value={draft.ops.departureCapacity} onChange={(event) => setDraft((current) => ({ ...current, ops: { ...current.ops, departureCapacity: Number(event.target.value) } }))} />
            </label>
            <label>
              进港容量/h
              <input type="number" min={0} value={draft.ops.arrivalCapacity} onChange={(event) => setDraft((current) => ({ ...current, ops: { ...current.ops, arrivalCapacity: Number(event.target.value) } }))} />
            </label>
            <label>
              最小过站 min
              <input type="number" min={0} value={draft.ops.minimumTurnMinutes} onChange={(event) => setDraft((current) => ({ ...current, ops: { ...current.ops, minimumTurnMinutes: Number(event.target.value) } }))} />
            </label>
            <label>
              地服等级
              <input type="number" min={0} max={2} step={0.05} value={draft.ops.groundHandlingLevel} onChange={(event) => setDraft((current) => ({ ...current, ops: { ...current.ops, groundHandlingLevel: Number(event.target.value) } }))} />
            </label>
            <label>
              基础油价 $/t
              <input type="number" min={0} step={10} value={draft.fuel.basePriceUsdPerTon} onChange={(event) => setDraft((current) => ({ ...current, fuel: { ...current.fuel, basePriceUsdPerTon: Number(event.target.value) } }))} />
            </label>
            <label>
              税费指数
              <input type="number" min={0} max={3} step={0.01} value={draft.fuel.taxIndex} onChange={(event) => setDraft((current) => ({ ...current, fuel: { ...current.fuel, taxIndex: Number(event.target.value) } }))} />
            </label>
            <label>
              日供油 kg
              <input type="number" min={0} step={1000} value={draft.fuel.supplyLimitKgPerDay} onChange={(event) => setDraft((current) => ({ ...current, fuel: { ...current.fuel, supplyLimitKgPerDay: Number(event.target.value) } }))} />
            </label>
            <label>
              加油速率 kg/min
              <input type="number" min={0} step={50} value={draft.fuel.refuelRateKgPerMin} onChange={(event) => setDraft((current) => ({ ...current, fuel: { ...current.fuel, refuelRateKgPerMin: Number(event.target.value) } }))} />
            </label>
            <label>
              单班最小/最大 kg
              <input
                value={`${draft.fuel.minUpliftKg}/${draft.fuel.maxUpliftKg}`}
                onChange={(event) => {
                  const [minUpliftKg, maxUpliftKg] = event.target.value.split("/").map((value) => Number(value.trim()));
                  setDraft((current) => ({ ...current, fuel: { ...current.fuel, ...normalizedFuelUpliftRange(minUpliftKg, maxUpliftKg, current.fuel.maxUpliftKg) } }));
                }}
              />
            </label>
            <label>
              基础客流 pax/day
              <input type="number" min={0} value={draft.payload.baseDepartureDemand} onChange={(event) => setDraft((current) => ({ ...current, payload: { ...current.payload, baseDepartureDemand: Number(event.target.value) } }))} />
            </label>
            <label>
              旅客处理 pax
              <input type="number" min={0} value={draft.payload.paxProcessingCapacity} onChange={(event) => setDraft((current) => ({ ...current, payload: { ...current.payload, paxProcessingCapacity: Number(event.target.value) } }))} />
            </label>
            <label>
              货邮指数
              <input type="number" min={0} max={4} step={0.05} value={draft.payload.cargoDemandIndex} onChange={(event) => setDraft((current) => ({ ...current, payload: { ...current.payload, cargoDemandIndex: Number(event.target.value) } }))} />
            </label>
            <label>
              行李 kg/pax
              <input type="number" min={0} step={1} value={draft.payload.baggageKgPerPassenger} onChange={(event) => setDraft((current) => ({ ...current, payload: { ...current.payload, baggageKgPerPassenger: Number(event.target.value) } }))} />
            </label>
            <label>
              货邮处理 kg
              <input type="number" min={0} step={100} value={draft.payload.cargoProcessingCapacityKg} onChange={(event) => setDraft((current) => ({ ...current, payload: { ...current.payload, cargoProcessingCapacityKg: Number(event.target.value) } }))} />
            </label>
            <button className="primary-command gm-airport-save" onClick={() => onSaveAirportConfig({ ...draft, airportCode: selectedAirport.code })}>
              <Save size={15} />
              保存机场运行数据
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

type MiniStatTone = "neutral" | "good" | "warn" | "bad";
type AirportFuelQuote = ReturnType<typeof buildAirportFuelQuote>;

function averageAirportFuelPrice(fuelConfigs: AirportConfigSet["fuel"]): number {
  if (fuelConfigs.length === 0) {
    return 0;
  }
  const totalWeightedFuelPrice = fuelConfigs.reduce((sum, config) => sum + config.basePriceUsdPerTon * config.taxIndex, 0);
  return Math.round(totalWeightedFuelPrice / fuelConfigs.length);
}

function airportEvents(airport: WorldData["airports"][number] | undefined, activeEvents: GlobalEvent[]): GlobalEvent[] {
  if (!airport) {
    return [];
  }
  return activeEvents.filter((event) => eventMatchesAirport(event, airport));
}

function airportFuelQuote(airport: WorldData["airports"][number] | undefined, activeEvents: GlobalEvent[], airportConfigs: AirportConfigSet): AirportFuelQuote | undefined {
  if (!airport) {
    return undefined;
  }
  const fuelConfig = airportConfigs.fuel.find((config) => config.airportCode === airport.code);
  if (!fuelConfig) {
    return undefined;
  }
  return buildAirportFuelQuote(airport, activeEvents, fuelConfig);
}

function selectedAirportCapacityMultiplier(airport: WorldData["airports"][number] | undefined, activeEvents: GlobalEvent[]): number {
  if (!airport) {
    return 1;
  }
  return airportCapacityMultiplier(airport, activeEvents);
}

function airportGroundModel(draft: AirportConfigUpdate): {
  serviceFactor: string;
  fuel10tMinutes: string;
  defuel10tMinutes: string;
  paxWaveMinutes: string;
  cargo10tMinutes: string;
  delayModel: string;
} {
  return {
    serviceFactor: groundServiceFactorLabel(draft.ops.groundHandlingLevel),
    fuel10tMinutes: fuelTenTonneServiceMinutes(draft.fuel.refuelRateKgPerMin),
    defuel10tMinutes: defuelTenTonneServiceMinutes(draft.fuel.refuelRateKgPerMin),
    paxWaveMinutes: passengerWaveServiceMinutes(draft.payload.paxProcessingCapacity),
    cargo10tMinutes: cargoTenTonneServiceMinutes(draft.payload.cargoProcessingCapacityKg),
    delayModel: groundDelayModelLabel(draft.ops.groundHandlingLevel)
  };
}

function groundServiceFactorLabel(groundHandlingLevel: number): string {
  if (groundHandlingLevel <= 0) {
    return "--";
  }
  return `x${groundHandlingLevel.toFixed(2)}`;
}

function fuelTenTonneServiceMinutes(refuelRateKgPerMin: number): string {
  if (refuelRateKgPerMin <= 0) {
    return "--";
  }
  const tenTonneFuelKg = 10_000;
  const transferMinutes = Math.ceil(tenTonneFuelKg / refuelRateKgPerMin);
  const setupBufferMinutes = 5;
  return `${transferMinutes + setupBufferMinutes}m`;
}

function defuelTenTonneServiceMinutes(refuelRateKgPerMin: number): string {
  if (refuelRateKgPerMin <= 0) {
    return "--";
  }
  const tenTonneFuelKg = 10_000;
  const rawDefuelRateKgPerMin = Math.round(refuelRateKgPerMin * 0.45);
  const protectedDefuelRateKgPerMin = Math.max(80, rawDefuelRateKgPerMin);
  const transferMinutes = Math.ceil(tenTonneFuelKg / protectedDefuelRateKgPerMin);
  const safetyBufferMinutes = 18;
  return `${transferMinutes + safetyBufferMinutes}m`;
}

function passengerWaveServiceMinutes(paxProcessingCapacity: number): string {
  if (paxProcessingCapacity <= 0) {
    return "--";
  }
  const targetPassengers = 180;
  const rawBoardingRatePerWave = Math.round(paxProcessingCapacity / 10);
  const protectedBoardingRatePerWave = Math.max(20, rawBoardingRatePerWave);
  const waveCount = Math.ceil(targetPassengers / protectedBoardingRatePerWave);
  const minutesPerWave = 5;
  const doorAndQueueBufferMinutes = 10;
  return `${waveCount * minutesPerWave + doorAndQueueBufferMinutes}m`;
}

function cargoTenTonneServiceMinutes(cargoProcessingCapacityKg: number): string {
  if (cargoProcessingCapacityKg <= 0) {
    return "--";
  }
  const tenTonneCargoKg = 10_000;
  const rawCargoRateKgPerWave = Math.round(cargoProcessingCapacityKg / 18);
  const protectedCargoRateKgPerWave = Math.max(450, rawCargoRateKgPerWave);
  const waveCount = Math.ceil(tenTonneCargoKg / protectedCargoRateKgPerWave);
  const minutesPerWave = 5;
  const cargoDocumentBufferMinutes = 8;
  return `${waveCount * minutesPerWave + cargoDocumentBufferMinutes}m`;
}

function closedAirportTone(closedCount: number): MiniStatTone {
  if (closedCount > 0) {
    return "bad";
  }
  return "good";
}

function noFuelAirportTone(noFuelCount: number): MiniStatTone {
  if (noFuelCount > 0) {
    return "warn";
  }
  return "good";
}

function airportConfigRowClassName(airportCode: string, selectedAirportCode: string): string {
  if (airportCode === selectedAirportCode) {
    return "selected-row";
  }
  return "";
}

function selectedFuelPriceLabel(fuelQuote: AirportFuelQuote | undefined): string {
  if (fuelQuote) {
    return `当前油价 $${fuelQuote.priceUsdPerTon}/t`;
  }
  return "当前油价 --";
}

function selectedFuelAvailabilityLabel(fuelQuote: AirportFuelQuote | undefined): string {
  if (fuelQuote?.available) {
    return "当前供油";
  }
  return "当前停供";
}

function selectedAirportEventLabel(eventCount: number): string {
  if (eventCount > 0) {
    return `叠加 ${eventCount} 个活动事件`;
  }
  return "无活动事件叠加";
}

function groundDelayModelLabel(groundHandlingLevel: number): string {
  if (groundHandlingLevel < 0.75) {
    return "高扰动";
  }
  if (groundHandlingLevel < 1) {
    return "中扰动";
  }
  return "低扰动";
}

function airportOpsStatusTone(closed: boolean): string {
  if (closed) {
    return "bad";
  }
  return "good";
}

function airportOpsStatusLabel(closed: boolean): string {
  if (closed) {
    return "关闭";
  }
  return "开放";
}

function airportFuelStatusTone(available: boolean): string {
  if (available) {
    return "good";
  }
  return "bad";
}

function airportFuelStatusLabel(available: boolean): string {
  if (available) {
    return "供油";
  }
  return "停供";
}

function airportFuelSummary(update: AirportConfigUpdate): string {
  const indexedFuelPriceUsdPerTon = Math.round(update.fuel.basePriceUsdPerTon * update.fuel.taxIndex);
  const maxUpliftTons = Math.round(update.fuel.maxUpliftKg / 1000);
  return `$${indexedFuelPriceUsdPerTon}/t / ${maxUpliftTons}t`;
}

function normalizedFuelUpliftRange(
  minUpliftKg: number,
  maxUpliftKg: number,
  previousMaxUpliftKg: number
): Pick<AirportConfigUpdate["fuel"], "minUpliftKg" | "maxUpliftKg"> {
  const normalized = {
    minUpliftKg: 0,
    maxUpliftKg: previousMaxUpliftKg
  };
  if (Number.isFinite(minUpliftKg)) {
    normalized.minUpliftKg = minUpliftKg;
  }
  if (Number.isFinite(maxUpliftKg)) {
    normalized.maxUpliftKg = maxUpliftKg;
  }
  return normalized;
}
